import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { setUnauthorizedHandler } from "@/lib/api/client";
import { queryKeys } from "@/lib/query/keys";
import { hasSession, onTokensChanged } from "@/lib/auth/tokens";
import {
  fetchMe,
  login as loginApi,
  logout as logoutApi,
} from "@/lib/auth/auth-api";
import type { CurrentUser } from "@/lib/auth/roles";
import { applyAccountLanguage } from "@/lib/auth/account-language";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  user: CurrentUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // Tracks whether the app believes a session exists. Seeded from persisted
  // tokens so a reload bootstraps via /auth/me (the 401→refresh→retry path in
  // the API client transparently re-mints the access token).
  const [sessionActive, setSessionActive] = useState<boolean>(() => hasSession());

  const meQuery = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchMe,
    enabled: sessionActive,
    retry: false,
    staleTime: Infinity,
  });

  // Tokens can arrive without anyone calling `login()` — the enrolment
  // verification link mints a JWT pair directly, which is the whole point of a
  // journey with no password. `sessionActive` is seeded once at mount, so
  // without this the provider stays `unauthenticated` while a perfectly good
  // token sits in storage, and the route guard bounces the participant to a
  // sign-in screen they have no password for. `onTokensChanged` existed for
  // this and had no subscriber.
  useEffect(() => onTokensChanged(() => setSessionActive(hasSession())), []);

  // When the API client gives up refreshing (refresh expired/invalid), drop to
  // unauthenticated and purge cached user data. Route guards react to this.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSessionActive(false);
      queryClient.removeQueries({ queryKey: queryKeys.auth.all });
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  // A failed /auth/me (e.g. refresh token rejected) also means no session.
  useEffect(() => {
    if (meQuery.isError) {
      setSessionActive(false);
    }
  }, [meQuery.isError]);

  const login = useCallback(
    async (credentials: { email: string; password: string }) => {
      await loginApi(credentials);
      setSessionActive(true);
      // Tokens are set; fetch the user immediately so the shell is ready.
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
      // The query is not enabled yet on a fresh sign-in (sessionActive flips on
      // the next render), so fetch the account rather than read the cache. A
      // failure here is not a failed sign-in: the me query reports it, as it
      // always has.
      try {
        const me = await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchMe,
        });
        applyAccountLanguage(me);
      } catch {
        // Reported through meQuery; signing in carries on.
      }
      // Whoever was here before left their data in the cache, and the default
      // `staleTime` is 30s — long enough for the next person to sign in on a
      // shared machine and be shown the previous one's dashboard, action plans
      // or billing before anything refetches. `/auth/me` is exempt because the
      // line above has just refetched it; dropping it here would undo that.
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== queryKeys.auth.all[0],
      });
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    logoutApi();
    setSessionActive(false);
    // Everything, not just the auth keys: signing out has to leave nothing of
    // this person behind for whoever uses the browser next, and there is no
    // session left for any of it to belong to.
    queryClient.clear();
  }, [queryClient]);

  const status: AuthStatus = !sessionActive
    ? "unauthenticated"
    : meQuery.data
      ? "authenticated"
      : meQuery.isError
        ? "unauthenticated"
        : "loading";

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      status,
      isAuthenticated: status === "authenticated",
      login,
      logout,
    }),
    [meQuery.data, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Whether a session is signed in, without requiring an <AuthProvider>: false
 * outside one. For components that also render on screens mounted without
 * the provider, such as the language switcher on the sign-in screen.
 */
export function useIsAuthenticated(): boolean {
  return useContext(AuthContext)?.isAuthenticated ?? false;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
